import React from 'react';

import uploadFileIcon from '@assets/img/icons/upload-file.svg';
import selectFiles from '@chenyuwang/select-files';
import { UploadImagePreview } from '@components/Forms/UploadImagePreview';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { LocalFile } from '@models/next/FileType';
import { MobxZodArrayField, getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { ZodArray } from 'zod';

const strings = createStrings({
  ja: {
    selectFile: 'ファイルを選択',
    photo: '画像',
    maxFileSize: 'ファイルサイズを超える',
    maxNumber: 'ファイル数を超える',
    acceptType: 'ファイルタイプが正しくありません',
    resolution: '解像度が正しくありません',
    prompt: 'ファイルをドロップしてください',
    uploadTypeError: '画像またはPDFだけがアップロードできます。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    paddingBottom: 11,
    paddingTop: '0.58rem',
  },
  label: {
    ...common.fontWeight500,
    color: theme.palette.common.black,
    paddingTop: 3,
    width: 170,
  },
  body: {
    flex: 1,
    paddingRight: 0,
    ...common.flexColumn,
  },
  bodyRow: {
    ...common.flexInline,
  },
  buttonPadding: {
    padding: '6px 30px !important',
  },
  images: {
    ...common.multilineContainer,
    gap: '10px',
    paddingTop: '6px',
  },
  errorText: {
    ...common.exSmallerText,
    color: theme.palette.error.main,
    marginTop: 5,
  },
  helperText: {
    ...common.smallerText,
    color: theme.palette.grey[700],
    marginTop: 5,
  },
  dragContainer: {
    ...common.flexColumn,
    ...common.baseButton,
    rowGap: '0.5rem',
    position: 'relative',
    padding: '16px 22px',
    minHeight: 175,
    backgroundColor: theme.palette.grey[50],
    border: `1px dashed ${theme.palette.grey[400]}`,
  },
  background: {
    ...common.absoluteFill,
    ...common.flexCenter,
    ...common.middleText,
    ...common.textGray600,
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

export interface MultiImageUploadInputProps {
  field: MobxZodArrayField<ZodArray<typeof LocalFile>>;
  buttonTitle?: string;
  helperText?: string;
}

export const MultiImageUploadInput: React.FC<MultiImageUploadInputProps> = observer((props) => {
  const { field, helperText = '' } = props;

  const images = getDecodeResult(field.decodeResult) ?? [];

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderHelperText = (text: string) => {
    return <div className={classes.helperText}>{text}</div>;
  };

  const onSelectFile = async () => {
    const files = await selectFiles({
      accept: 'image/*,application/pdf',
      multiple: true,
    });

    field.setTouched(true);
    field.push(...[...(files ?? [])].map((file) => ({ file, remote: undefined })));
  };

  const onDropContainer: React.DragEventHandler<HTMLDivElement> = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();

    const files: LocalFile[] = [];

    for (const item of ev.dataTransfer.items) {
      const file = item.getAsFile();
      if (file) {
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
          files.push({
            file,
            remote: undefined,
          });
        } else {
          alert(strings.uploadTypeError);
          return;
        }
      }
    }

    field.setTouched(true);
    field.push(...files);
  };

  return (
    <div className={classes.body}>
      <div
        className={classes.dragContainer}
        onClick={onSelectFile}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropContainer}
      >
        <div className={classes.background}>
          <div className={clsx(classes.flexColumn, classes.itemsCenter, classes.fontWeight600)}>
            <img src={uploadFileIcon} alt="Upload File Icon" />
            <div style={{ width: '0.75rem' }} />
            {strings.prompt}
          </div>
        </div>
      </div>

      <div className={classes.bodyRow}>
        <div className={classes.images}>
          {images.map((image, index) => (
            <UploadImagePreview
              key={`${index}${image}`}
              image={image}
              title={`${strings.photo}${index + 1}`}
              onRemove={() => field.splice(index, 1, [])}
            />
          ))}
        </div>
      </div>

      <ErrorMessages field={field} />

      <div className={classes.bodyRow}>{renderHelperText(helperText)}</div>
    </div>
  );
});
